/**
 * BgtSpcRtsoExpndDialog.tsx
 * ------------------------------------------------------------
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";

import Button from "@/js/common/ui/Button";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";

import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";

import BgtEvdncViewDialog from "./BgtEvdncViewDialog";
import BgtAtrzRejectReason from "./BgtAtrzRejectReason";
import { budgetFilesProps } from "@/js/common/types/types";
import BudgetViewFile from "../../budgetFileBox/BudgetViewFile";
import BgtSpcExpndViewDialog from "../../bgtspc/dialog/BgtSpcExpndViewDialog";

interface Props {
  spcAcntgExpndDocId: string;
  atrzId: string;
  atrzLineId: string;
  setCallTime: Function;
  onDone?: () => void;
}

const BgtSpcRtsoExpndDialog = ({ spcAcntgExpndDocId, atrzId, setCallTime, atrzLineId, onDone}: Props) => {
  // ------------------------------------------------------------
  // 지출 문서 기본 정보
  // ------------------------------------------------------------
  const [expndRsdocNo      , setExpndRsdocNo      ] = React.useState<string>(""); // 결의서 문서번호
  const [implDt, setImplDt] = React.useState("");
  const [kwanCd, setKwanCd] = React.useState("");
  const [hangCd, setHangCd] = React.useState("");
  const [mokCd, setMokCd] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [summary, setSummary] = React.useState("");
  const [memo, setMemo] = React.useState("");
  const [expndRsdocSeCd    , setExpndRsdocSeCd    ] = React.useState<string>(""); // 지출결의서 구분 코드
  const [spcAcntgSeCd, setSpcAcntgSeCd] = React.useState(""); // 특별회계 구분 코드
  const [lnkgSpcAcntgExpndDocId, setLnkgSpcAcntgExpndDocId] = React.useState<string>(""); // 연결 지출결의서 문서 ID
  const [lnkgExpndRsdocNo, setLnkgExpndRsdocNo] = React.useState<string>(""); // 연결 지출결의서 문서번호
  const [spcExpndModalOpen     , setSpcExpndModalOpen     ] = React.useState<boolean>(false); // 지출결의서 상세정보 모달


  // (여입 UI 제거) 값은 받아도 화면에서는 사용하지 않음
  const [_yeoipYn, setYeoipYn] = React.useState(false);

  // ------------------------------------------------------------
  // 증빙 리스트 + 증빙 상세 모달
  // ------------------------------------------------------------
  const [evdncArr, setEvdncArr] = React.useState<any[]>([]);
  const [evdncModalOpen, setEvdncModalOpen] = React.useState(false);
  const [spcAcntgExpndEvdncId, setSpcAcntgExpndEvdncId] = React.useState("");
  const [savedSpcExpndFiles, setSavedSpcExpndFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일
  // table hover
  const [hoveredRow, setHoveredRow] = React.useState<number | null>(null);

  // ------------------------------------------------------------
  // 처리중(중복 클릭 방지)
  // ------------------------------------------------------------
  const [confirming, setConfirming] = React.useState(false);

  // ------------------------------------------------------------
  // 반려사유 모달
  // ------------------------------------------------------------
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);

  const clearForm = () => {
    setExpndRsdocNo("");
    setLnkgExpndRsdocNo("");
    setImplDt("");
    setKwanCd("");
    setHangCd("");
    setMokCd("");
    setAmount("");
    setSummary("");
    setMemo("");
    setYeoipYn(false);
    setEvdncArr([]);
    setHoveredRow(null);

    setSpcAcntgExpndEvdncId("");
    setEvdncModalOpen(false);
  };

  // ------------------------------------------------------------
  // 지출 상세 조회 (기존 로직 유지)
  // ------------------------------------------------------------
  React.useEffect(() => {
    if (Util.isEmpty(spcAcntgExpndDocId)) {
      clearForm();
      return;
    }

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/atrz/spc/select-aprvr-spc-expnd-dtl.do",
      methodType: "post",
      paramType: "json",
      paramData: { spcAcntgExpndDocId: String(spcAcntgExpndDocId) },
      onSuccessFn: (res: any) => {
        const result = res?.result || {};
        const item = result?.item || {};
        const list = result?.list || [];

        const implDtRaw = String(item.implDt);
        setImplDt(Util.cvtDateStrForm(implDtRaw, "-", "YYYYMMDD"));
        setExpndRsdocNo(String(item.expndRsdocNo));
        setKwanCd(String(item.bgtSbjct1stCd));
        setHangCd(String(item.bgtSbjct2stCd));
        setMokCd(String(item.bgtSbjct3stCd));
        setAmount(String(item.implAmt));
        setSummary(String(item.abstrCn));
        setMemo(String(item.memo));
        setSpcAcntgSeCd(item.spcAcntgSeCd); // 특별회계 구분 코드 
        setExpndRsdocSeCd(item.expndRsdocSeCd); // 지출결의서 구분 코드
        setLnkgSpcAcntgExpndDocId(item.lnkgSpcAcntgExpndDocId); // 연결 문서번호
        setSavedSpcExpndFiles(res.result.listFile); // 첨부파일


        setEvdncArr(list);

        AppContext.hideSpinner();
      },
    });
  }, [spcAcntgExpndDocId]);

  // ------------------------------------------------------------
  // 공통 파라미터 검증 (기존 로직 유지)
  // ------------------------------------------------------------
  const validateBase = () => {
    if (Util.isEmpty(atrzId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzId)가 없습니다.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(atrzLineId)) {
      AppContext.openAlert({ title: "알림", msg: "결재 정보(atrzLineId)가 없습니다.", icon: "warning" });
      return false;
    }
    if (Util.isEmpty(spcAcntgExpndDocId)) {
      AppContext.openAlert({ title: "알림", msg: "대상이 선택되지 않았습니다.", icon: "warning" });
      return false;
    }
    return true;
  };

  // ------------------------------------------------------------
  // 증빙 상세 열기 (기존 로직 유지)
  // ------------------------------------------------------------
  const openDetailEvdnc = (id: string) => {
    if (Util.isEmpty(id)) return;
    setSpcAcntgExpndEvdncId(String(id));
    setEvdncModalOpen(true);
  };

  // ------------------------------------------------------------
  // 확인(결재확인: 01) (기존 로직 유지)
  // ------------------------------------------------------------
  const handleConfirm = () => {
    if (confirming) return;
    if (!validateBase()) return;

    setConfirming(true);
    AppContext.showSpinner();

    Axios.dataAccess({
      url: "system/atrz/process-aprvr-idnty-rjct.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        atrzId: String(atrzId),
        atrzLineId: String(atrzLineId),
        atrzLinkDocId: String(spcAcntgExpndDocId),
        atrzLinkDocIdntySeCd: "01",
      },
      onSuccessFn: () => {
        AppContext.hideSpinner();
        setConfirming(false);

        AppContext.openAlert({ title: "알림", msg: "확인 처리되었습니다.", icon: "info" });
        if (typeof setCallTime === "function") setCallTime(new Date());
        onDone?.();
      },
    });
  };

  // ------------------------------------------------------------
  // 반려 버튼 클릭 -> 반려사유 모달 오픈 (기존 로직 유지)
  // ------------------------------------------------------------
  const openReject = () => {
    if (!validateBase()) return;
    setRejectModalOpen(true);
  };

  // ------------------------------------------------------------
  // 증빙 합계(리스트 기반)
  // ------------------------------------------------------------
  const sumUseAmt = React.useMemo(() => {
    if (!Array.isArray(evdncArr) || evdncArr.length === 0) return "0";
    const s = evdncArr.reduce((acc: number, r: any) => acc + Number(r?.useAmt || 0), 0);
    return String(s);
  }, [evdncArr]);

  return (
    <div className="w100p dpFlx jcC pt5">
      <div className="dpFlx aiFs">
        <div className="w400">
          <div className="mb10 dpFlx jcSb">
            <div>
                <span className="fm-b fs16 fc007bff ls1 fw700">지출결의서 상세내용</span>
            </div>
            <div className={
                    "dpFlx bd1 bdcE8E8ED aiC gap8 pw12 h24 bdrd20 " +
                    ( expndRsdocSeCd === "01" ? "bgcDEDEDE" // 일반(gray)
                    : expndRsdocSeCd === "02" ? "bgcFCA852" // 무증빙(yellow)
                    : expndRsdocSeCd === "03" ? "bgc96c832" // 여입(grean)
                    : expndRsdocSeCd === "04" ? "bgc87C6FD" // 자료제출(blue)
                    : "")
                }
            >
                <span className="fm-b fs13">
                    { expndRsdocSeCd === "01" ? "일반"
                    : expndRsdocSeCd === "02" ? "무증빙"
                    : expndRsdocSeCd === "03" ? "여입"
                    : expndRsdocSeCd === "04" ? "자료제출"
                    : ""}
                </span>
            </div>

            {/* 지출결의서 문서 ID */}
            <div>
                <span className="fm-b fs13 ls06 fc777777 mr10">{expndRsdocNo}</span>
            </div>
        </div>

        {/* 연결 문서번호 */}
        { (expndRsdocSeCd === "03" || expndRsdocSeCd === "04") && (     // 여입, 자료제출
          <div className="flx1">
              <div className="h30 dpFlx aiE pb5">
                  <span className="fm-b fs13 ls06 fc111111">연결 문서번호</span>
              </div>
              <div className="h36 aiC bsBdb bd1 bdcDDDDDD bdrd3 bgcF0F0F0 csP taC pt2 op70-hover" onClick={() => setSpcExpndModalOpen(true)}>
                  <span className="fm-c ls1 fs14 fc333333">{lnkgExpndRsdocNo}</span>
              </div>
          </div>
        )}
        
          {/* 1행: 행위년월일 / 금액 (196+196) */}
          <div className="dpFlx mb5" style={{ width: 400, gap: 8 }}>
            <div className="dpFlx flxdCol" style={{ width: 196 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">행위년월일</span>
              </div>
              <DatePickerBox
                value={implDt}
                width={196}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                bgColor="#F6F6F6"
                textAlign="left"
                calendarThemeColor={AppContext.themeColor}
                onChange={(data: string) => setImplDt(data)}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 196 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">금액</span>
              </div>
              <div className="h36 dpFlx aiC bsBdb" style={{ width: 196 }}>
                <NumberTextBox
                  value={amount}
                  className="h36 bd1 bdrd2 bgcFFE6CE"
                  style={{ width: 196 }}
                  thousandsGroupStyle="thousand"
                  thousandSeparator=","
                  placeholder="0"
                  fontSize={14}
                  fontFamily="fm-c"
                  letterSpacing={-1}
                  inputClass="taR mr10"
                  onChange={(v: string) => setAmount(v)}
                  disabled={true}
                />
              </div>
            </div>
          </div>

          {/* 2행: 관/항/목 (128*3) */}
          <div className="dpFlx mb5" style={{ width: 400, gap: 8 }}>
            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">관</span>
              </div>
              <SelectBox
                value={kwanCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt1CodeArray(spcAcntgSeCd, "02"))}
                onChangeFunc={(v: string) => {
                  setKwanCd(v);
                  setHangCd("");
                  setMokCd("");
                }}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">항</span>
              </div>
              <SelectBox
                value={hangCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt2CodeArray(spcAcntgSeCd, kwanCd))}
                onChangeFunc={(v: string) => {
                  setHangCd(v);
                  setMokCd("");
                }}
                disabled={true}
              />
            </div>

            <div className="dpFlx flxdCol" style={{ width: 128 }}>
              <div className="h30 dpFlx aiE pb5">
                <span className="fm-b fs13 ls06 fc111111">목</span>
              </div>
              <SelectBox
                value={mokCd}
                width={128}
                height={36}
                fontSize={14}
                fontFamily="fm-c"
                color="#333"
                letterSpacing={-1}
                borderColor="#ddd"
                borderOnColor="#222"
                borderRadius={2}
                background="#F6F6F6"
                paddingRight={8}
                codeArray={[{ code: "", name: "선택" }].concat(Util.getSpcBgt3CodeArray(spcAcntgSeCd, hangCd))}
                onChangeFunc={(v: string) => setMokCd(v)}
                disabled={true}
              />
            </div>
          </div>

          {/* 적요 */}
          <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
            <div className="h30 dpFlx aiE pb5">
              <span className="fm-b fs13 ls06 fc111111">적요</span>
            </div>
            <TextBox
              value={summary}
              width={400}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#F6F6F6", padding: "0 12px" }}
              onChangeFunc={(t: string) => setSummary(t)}
              mode="disabled"
            />
          </div>

          {/* 메모 */}
          <div className="dpFlx flxdCol mb5" style={{ width: 400 }}>
            <div className="h30 dpFlx aiE pb5">
              <span className="fm-b fs13 ls06 fc111111">메모</span>
            </div>
            <TextBox
              value={memo}
              width={400}
              height={36}
              fontSize={14}
              fontFamily="fm-c"
              color="#333"
              letterSpacing={-1}
              borderColor="#ddd"
              borderOnColor="#222"
              borderRadius={2}
              style={{ backgroundColor: "#F6F6F6", padding: "0 12px" }}
              onChangeFunc={(t: string) => setMemo(t)}
              mode="disabled"
            />
          </div>
          
          {/* 첨부파일 */}
            <BudgetViewFile
                fileSeCd         = "B02" // 파일구분코드(특별회계기금_지출결의서[B02])
                savedBudgetFiles = {savedSpcExpndFiles}
                width = "400px"
                height = "70px"
            />

          {/* ===================== 지출증빙 리스트(테이블 디자인) ===================== */}
          <div className="mb10 mt5" style={{ width: 400 }}>
            <div className="h30 dpFlx jcSb aiE pb5">
              <span className="fm-b fs13 ls06 fc111111 mb5">지출증빙 내역서 목록</span>
            </div>

            <div className="w100p ofxA">
              <div style={{ minWidth: 320 }}>
                <div className="bd1 bdcDDDDDD bdrd2 bgcWhite">
                  <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <colgroup>
                      <col className="w100" />
                      <col className="w120" />
                      <col className="w100" />
                    </colgroup>

                    <thead>
                      <tr className="bgcf0f8ff h30" style={{ borderBottom: "1px solid #ccc" }}>
                        <th className="fm-a fs12 fc555555">사용일자</th>
                        <th className="fm-a fs12 fc555555">사용목적</th>
                        <th className="fm-a fs12 fc555555">금액</th>
                      </tr>
                    </thead>

                    <tbody>
                      {evdncArr.length === 0 && (
                        <tr style={{ borderBottom: "1px solid #ccc" }}>
                          <td colSpan={3} className="h28 taC fm-c fs12 fc999999">
                            조회된 지출증빙내역서가 없습니다.
                          </td>
                        </tr>
                      )}

                      {evdncArr.map((row: any, index: number) => {
                        const id = String(row.spcAcntgExpndEvdncId || "");
                        const useDt = String(row.useDt || "");
                        const usePrpsCn = String(row.usePrpsCn || "");
                        const useAmt = String(row.useAmt || "0");

                        return (
                          <tr
                            key={`${index}-${id}`}
                            className="csP"
                            onMouseOver={() => setHoveredRow(index)}
                            onMouseOut={() => setHoveredRow(null)}
                            style={{
                              borderBottom: "1px solid #ccc",
                              backgroundColor: hoveredRow === index ? "#EDF2FD" : "white",
                            }}
                            onClick={() => openDetailEvdnc(id)} 
                          >
                            <td className="h28 taC fm-c fs12 fc828282">{Util.cvtDateStrForm(useDt, "-", "YYYYMMDD")}</td>
                            <td className="h28 taL fm-c fs12 fc828282">
                              <span className="dpInBlock ellipsis w120">{usePrpsCn}</span>
                            </td>
                            <td className="h28 taC fm-c fs12 fc828282 taR pr5">{Util.comma(useAmt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>

                    <tfoot>
                      <tr className="h28 bgcDCDCDC">
                        <td colSpan={2} className="fm-b fs13 ls06 taC">
                          합계
                        </td>
                        <td className="taR fm-c fs13 ls06 pr5">{Util.comma(sumUseAmt)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* ===================== //지출증빙 리스트 끝 ===================== */}

          <div className="dpFlx jcR mt25 gap10">
            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              color="#fff"
              backgroundColor="crimson"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName="반려"
              onClickFunc={openReject}
              disabled={confirming}
            />

            <Button
              width={100}
              height={40}
              fontFamily="fm-b"
              fontSize={16}
              letterSpacing={-1}
              color="#fff"
              backgroundColor="#007bff"
              borderRadius={6}
              buttonClass="dpFlx aiC jcC"
              buttonName={confirming ? "처리중..." : "확인"}
              onClickFunc={handleConfirm}
              disabled={confirming}
            />
          </div>
        </div>
      </div>

      <Modal
        modalOpen={evdncModalOpen}
        dim={true}
        visible={true}
        titleEl={<DialogTitleBar title="지출증빙내역서" backgroundColor={AppContext.themeColor} />}
        onCloseFunc={() => setEvdncModalOpen(false)}
      >
        <BgtEvdncViewDialog
          mbsdsExpndDocId={spcAcntgExpndDocId}
          spcAcntgExpndEvdncId={spcAcntgExpndEvdncId}
          setModalOpen={setEvdncModalOpen}
          mode="SPC"
        />
      </Modal>

      <Modal
        modalOpen={rejectModalOpen}
        dim={true}
        visible={true}
        titleEl={<DialogTitleBar title="반려 사유 입력" backgroundColor={AppContext.themeColor} />}
        onCloseFunc={() => setRejectModalOpen(false)}
      >
        <BgtAtrzRejectReason
          atrzId={String(atrzId)}
          atrzLineId={String(atrzLineId)}
          atrzLinkDocId={String(spcAcntgExpndDocId)}
          setModalOpen={setRejectModalOpen}
          setCallTime={setCallTime}
          onDone={onDone}
        />
      </Modal>
      {/* 연결 지출결의서 상세정보 모달 */}
        <Modal modalOpen={spcExpndModalOpen} dim={true} visible={true}
            titleEl={<DialogTitleBar title="지출결의서 상세내용"/>}
            onCloseFunc={() => {
                setSpcExpndModalOpen(false);
            }}
        >
            <div className="h700">
                <BgtSpcExpndViewDialog
                    spcAcntgExpndDocId   = {lnkgSpcAcntgExpndDocId}     // 지출결의서 문서 ID
                    systemCode           = {spcAcntgSeCd}
                    setSpcExpndModalOpen = {setSpcExpndModalOpen}
                />
            </div>
        </Modal>
    </div>
  );
};

export default BgtSpcRtsoExpndDialog;
